import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canPerformAction } from '../utils/permissionUtils';
import { useLanguage } from '../contexts/LanguageContext';
import MetadataEditDialog from './MetadataEditDialog';
import '../styles/storage-management.css';

const StorageManagement = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showMetadataDialog, setShowMetadataDialog] = useState(false);
  const [selectedFileForMetadata, setSelectedFileForMetadata] = useState(null);

  // Check if user can manage storage
  const canManageStorage = canPerformAction(user, 'manage-storage');

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    }
  }, [isOpen]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/musicxml?operation=list`);
      const data = await response.json();
      setFiles(data.files || []);
    } catch (error) {
      console.error('Failed to load files:', error);
      alert(t('cloudStorage.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, allowOverwrite = false) => {
    if (!file.name.endsWith('.musicxml') && !file.name.endsWith('.xml')) {
      alert(t('storage.selectMusicXML'));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', user.id);
    formData.append('userRole', user.role);
    formData.append('allowOverwrite', allowOverwrite.toString());

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/musicxml?operation=upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (response.status === 409 && result.requiresOverwrite) {
        // File already exists, ask user for confirmation
        setUploading(false);
        if (window.confirm(t('storage.overwriteConfirm', { filename: result.filename }))) {
          // User confirmed, retry with overwrite flag
          handleFileUpload(file, true);
        }
        return;
      }
      
      if (result.success) {
        alert(t('storage.uploadSuccess'));
        loadFiles(); // Refresh the list
      } else {
        alert(t('storage.uploadFailed', { error: result.error }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(t('storage.uploadFailed', { error: 'Network error' }));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  // Called by MetadataEditDialog after an official PDF finishes uploading
  const handlePdfUploaded = (filename, pdfUrl) => {
    setFiles(prevFiles =>
      prevFiles.map(f =>
        f.filename === filename ? { ...f, pdfUrl } : f
      )
    );
  };

  const handleDownload = async (file) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/musicxml?operation=download&url=${encodeURIComponent(file.url)}`);
      
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const content = await response.text();
      
      // Create a blob with the file content
      const blob = new Blob([content], { type: 'application/xml' });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download error:', error);
      alert(t('storage.downloadFailed', { error: error.message }));
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(t('storage.deleteConfirm', { filename }))) {
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/musicxml?operation=delete&filename=${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userRole: user.role,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        alert(t('storage.deleteSuccess'));
        loadFiles(); // Refresh the list
      } else {
        alert(t('storage.deleteFailed', { error: result.error }));
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(t('storage.deleteFailed', { error: 'Network error' }));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return `0 ${t('storage.bytes')}`;
    const k = 1024;
    const sizes = [t('storage.bytes'), t('storage.kilobytes'), t('storage.megabytes')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const handleEditMetadata = (file) => {
    setSelectedFileForMetadata(file);
    setShowMetadataDialog(true);
  };

  const handleMetadataSave = (filename, metadata) => {
    // Update the files list with the new metadata
    setFiles(prevFiles =>
      prevFiles.map(file =>
        file.filename === filename
          ? { ...file, metadata }
          : file
      )
    );
  };

  if (!isOpen) return null;

  return (
    <div className="storage-management-overlay">
      <div className="storage-management-dialog">
        <div className="storage-management-header">
          <h2>{t('storage.title')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="storage-management-content">
          {/* Upload Section */}
          <div className="upload-section">
            <h3>{t('storage.uploadTitle')}</h3>
            <div 
              className={`upload-area ${dragOver ? 'drag-over' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input
                type="file"
                accept=".musicxml,.xml"
                onChange={handleFileInput}
                disabled={uploading}
                id="file-input"
                style={{ display: 'none' }}
              />
              <label htmlFor="file-input" className="upload-label">
                {uploading ? (
                  <div>{t('storage.uploading')}</div>
                ) : (
                  <div>
                    <div>{t('storage.dropFiles')}</div>
                    <div className="upload-hint">{t('storage.supportedFiles')}</div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Files List Section */}
          <div className="files-section">
            <div className="files-header">
              <h3>{t('storage.cloudFiles')}</h3>
              <button onClick={loadFiles} disabled={loading} className="refresh-button">
                {loading ? t('cloudStorage.loadingFiles') : t('storage.refresh')}
              </button>
            </div>

            {loading ? (
              <div className="loading">{t('cloudStorage.loadingFiles')}</div>
            ) : (
              <div className="files-list">
                {files.length === 0 ? (
                  <div className="no-files">{t('storage.noFiles')}</div>
                ) : (
                  files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-info">
                        <div className="file-name">
                          {file.metadata?.title || file.filename}
                          {file.metadata?.title && (
                            <span className="filename-secondary"> ({file.filename})</span>
                          )}
                        </div>
                        <div className="file-details">
                          {formatFileSize(file.size)} • {formatDate(file.uploadedAt)}
                          {file.metadata?.defaultTempoBPM && (
                            <span className="tempo-info"> • {file.metadata.defaultTempoBPM} BPM</span>
                          )}
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          onClick={() => handleEditMetadata(file)}
                          className="edit-metadata-button"
                        >
                          {t('metadata.editButton')}
                        </button>
                        <button
                          onClick={() => handleDownload(file)}
                          className="download-button"
                        >
                          {t('storage.download')}
                        </button>
                        <button
                          onClick={() => handleDelete(file.filename)}
                          className="delete-button"
                        >
                          {t('controls.delete')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metadata Edit Dialog */}
      {showMetadataDialog && (
        <MetadataEditDialog
          isOpen={showMetadataDialog}
          onClose={() => setShowMetadataDialog(false)}
          file={selectedFileForMetadata}
          onSave={handleMetadataSave}
          onPdfUploaded={handlePdfUploaded}
        />
      )}
    </div>
  );
};

export default StorageManagement;